import { Button, TextField } from '@mui/material'
import FormInputField from 'components/common/Formik/InputField'
import VideoSDK from 'components/VideoSKD'
import { toastContext } from 'context/ToastContext'
import { Form, Formik, useField } from 'formik'
import usePersistedCountdown from 'hooks/usePersistedCountdown'
import React, { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { io } from 'socket.io-client'
import getSocketUrl from 'helpers/socketUrl'
import * as Yup from 'yup'

const joinRequestResponseEventName = 'joinRequestResponse'

const initialValues = {
  name: '',
  telephone: '',
  email: '',
  dni: '',
}

const JoinRequest = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const { setToastOptions } = useContext(toastContext)
  const [isSubmittingRemainingTime, setIsSubmittingRemainingTime] = usePersistedCountdown({
    key: 'remainingTimeToJoinRequest',
  })
  const [joinRequestAuthInfo, setJoinRequestAuthInfo] = useState(null)
  const [requester, setRequester] = useState(null)

  const onSubmitForm = async (values) => {
    setRequester({
      name: values.name,
      dni: values.dni,
      email: values.email,
      telephone: values.telephone,
      mediatorId: queryParams.get('mediatorId'),
      roomId: queryParams.get('roomId'),
    })
    setIsSubmittingRemainingTime(30)
  }

  useEffect(() => {
    if (!requester) return
    const ws = io(getSocketUrl(), {
      query: { joinRequester: JSON.stringify(requester) },
      transports: ['websocket', 'polling'],
    })
    ws.on(joinRequestResponseEventName, (data) => {
      if (data.callNotFound) {
        setToastOptions({
          message: 'La llamada ya no está disponible',
          error: true,
        })
      } else if (!data.token) {
        setToastOptions({ message: 'No ha sido autorizado', error: true })
      } else setJoinRequestAuthInfo(data)
      setIsSubmittingRemainingTime(30)
    })
    return () => {
      ws.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requester])

  if (joinRequestAuthInfo?.token) {
    return (
      <main className='w-[100vw] h-[100vh]'>
        <VideoSDK
          redirectOnLeave='callFinishedGuest'
          token={joinRequestAuthInfo.token}
          meetingId={queryParams.get('roomId')}
          participantName={joinRequestAuthInfo.name}
          participantCanLeave={false}
        />
      </main>
    )
  }

  return (
    <div className='flex flex-row w-full pt-12'>
      <div className='py-12 flex-1'>
        <div
          className='flex bg-gray-100 rounded-lg shadow-2xl overflow-hidden
           mx-auto max-w-xs md:max-w-lg lg:max-w-xl'
        >
          <div className='w-full p-8 bg-purple-50'>
            <h1 className='mb-8 text-2xl font-semibold text-gray-600 text-center'>
              Solicitar Unirse
            </h1>
            <Formik
              initialValues={initialValues}
              validationSchema={validation}
              onSubmit={onSubmitForm}
              validateOnChange={false}
            >
              {({ values, errors }) => (
                <Form>
                  {/* {console.log({ values, errors })} */}
                  <div className='flex flex-col justify-center gap-4'>
                    <FormInputField label='Nombre completo' name='name' />
                    <FormInputField label='DNI' name='dni' />
                    <TelephoneField label='Teléfono' name='telephone' />
                    <FormInputField label='Email' name='email' />
                    <div className='m-2'>
                      <Button
                        className='text-white font-bold py-2 px-4 w-full rounded-lg'
                        type='submit'
                        disabled={!!isSubmittingRemainingTime}
                      >
                        {isSubmittingRemainingTime
                          ? `Espere ${isSubmittingRemainingTime} segundos para volver a solicitar`
                          : 'Enviar'}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  )
}

const validation = Yup.object().shape({
  name: Yup.string()
    .required('El nombre es requerido')
    .matches(
      /[a-zA-Z]+ [a-zA-Z]+/,
      'Por favor, ingrese un nombre y apellido válidos',
    ),
  dni: Yup.string()
    .required('El DNI es requerido')
    .matches(/\d{8,9}/, 'Por favor, ingrese un DNI válido'),
  telephone: Yup.string()
    .required('El teléfono es requerido')
    .matches(/^\+54 9 \d{1,5} \d{6,12}$/, {
      message: 'Por favor, ingrese teléfono y número de área válidos',
    }),
  email: Yup.string()
    .required('El email es requerido')
    .email('Por favor, ingrese un mail válido'),
})

export default JoinRequest

const TelephoneField = ({ name, id, label }) => {
  const [, meta, { setValue }] = useField(name)
  const [areaCode, setAreaCode] = useState('')
  const [telephone, setTelephone] = useState('')
  const isInvalid = !!meta.touched && !!meta.error

  useEffect(() => {
    if (areaCode || telephone) setValue(`+54 9 ${+areaCode} ${telephone}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaCode, telephone])

  return (
    <div key={name}>
      <label
        className='block text-gray-700 text-sm font-bold pt-2 pb-1'
        htmlFor={id || name}
      >
        {label}
      </label>
      <div className='flex flex-col md:flex-row items-center gap-4'>
        <div className='flex items-center gap-2 shrink-0'>
          <span className='whitespace-nowrap'>+54 (9)</span>
          <TextField
            className='z-0'
            fullWidth
            error={isInvalid}
            label='Código de área'
            onChange={(e) => setAreaCode(e.target.value)}
            variant='filled'
            value={areaCode}
            type='number'
          />
        </div>
        <TextField
          className='z-0'
          fullWidth
          error={isInvalid}
          label={label}
          onChange={(e) => setTelephone(e.target.value)}
          variant='filled'
          value={telephone}
          type='number'
        />
      </div>
      {isInvalid && <div className='text-red-500 text-sm'>{meta.error}</div>}
    </div>
  )
}
/*
<Autocomplete
          error={isInvalid}
          disablePortal
          id='telephone'
          options={options}

          fullWidth
          renderInput={(params) => {
            console.log(params)
            return (
              <TextField
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                {...params}
                variant='filled'
                label='Código de Área'
              />
            )
          }}
        />
*/
